import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { PlayingCard, CardEnhancement } from '../../engine/types'
import { SUIT_DISPLAY, RANK_DISPLAY } from '../../data/pokerConfig'
import { ENHANCEMENT_STYLES } from '../../data/enhancementConfig'
import { getCardFaceIconUrl, CARD_MASCOT_LABELS } from '../../data/cardIcons'
import { CardShell } from '../components'
import { cn } from '../utils/cn'

type Props = {
  card: PlayingCard
  selected?: boolean
  scoring?: boolean
  onClick?: () => void
  disabled?: boolean
  index?: number
  size?: 'sm' | 'md'
}

// ─── Tech Brand SVG icons (Iconify) ───

function EnhancementIcon({ type, size }: { type: CardEnhancement; size: number }) {
  const s = ENHANCEMENT_STYLES[type]
  if (type === 'none') return null

  const props = { width: size, height: size, viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' }

  // AWS (mdi:aws)
  if (type === 'foil') return (
    <svg {...props}>
      <path fill={s.color} d="M7.64 10.38c0 .25.02.45.07.62c.05.12.12.28.21.46c.04.04.05.1.05.15c0 .07-.04.13-.13.2l-.42.28c-.06.04-.12.06-.17.06c-.07 0-.13-.04-.2-.1c-.09-.1-.17-.2-.24-.31c-.06-.11-.13-.24-.2-.39c-.52.61-1.17.92-1.96.92c-.56 0-1-.16-1.33-.48c-.32-.32-.49-.75-.49-1.29c0-.55.2-1 .6-1.36c.41-.34.95-.52 1.63-.52c.23 0 .44.02.71.06c.23.03.5.08.76.14v-.48c0-.51-.1-.84-.31-1.07c-.22-.21-.57-.3-1.08-.3c-.24 0-.48.03-.72.08c-.25.06-.49.13-.72.23c-.11.04-.2.07-.23.08c-.05.02-.08.02-.11.02c-.09 0-.14-.06-.14-.2v-.33c0-.1.01-.18.05-.23q.045-.075.18-.12c.24-.14.51-.24.84-.32a4 4 0 0 1 1.04-.13q1.185 0 1.74.54c.37.36.55.91.55 1.64v2.15zm-2.7 1.02c.22 0 .44-.04.68-.12s.45-.23.63-.43c.11-.13.19-.27.25-.43c0-.16.05-.35.05-.58v-.27c-.2-.07-.4-.07-.62-.12a7 7 0 0 0-.62-.04c-.45 0-.77.09-.99.27s-.32.43-.32.76c0 .32.07.56.24.71c.16.17.39.25.7.25m5.34.71a.6.6 0 0 1-.28-.06c-.03-.05-.08-.14-.12-.26L8.32 6.65c-.04-.15-.06-.22-.06-.27c0-.11.05-.17.16-.17h.65c.13 0 .22.02.26.07c.06.04.1.13.14.26l1.11 4.4l1.04-4.4c.03-.13.07-.22.13-.26c.05-.04.14-.07.25-.07h.55c.12 0 .21.02.26.07c.05.04.1.13.13.26L14 11l1.14-4.46c.04-.13.09-.22.13-.26c.06-.04.14-.07.26-.07h.62c.11 0 .17.06.17.17c0 .03-.01.07-.02.12c0 0-.02.08-.04.15l-1.61 5.14c-.04.14-.08.21-.15.26c-.04.04-.13.07-.24.07h-.57c-.13 0-.19-.02-.27-.07a.45.45 0 0 1-.12-.26L12.27 7.5l-1.03 4.28q-.045.195-.12.27a.5.5 0 0 1-.27.06zm8.55.18c-.33 0-.7-.04-1.03-.12s-.59-.17-.76-.26a.5.5 0 0 1-.21-.19a.4.4 0 0 1-.04-.18v-.34c0-.14.05-.2.15-.2h.12c.04 0 .1.05.17.08c.22.1.47.18.73.23c.27.05.54.08.79.08c.42 0 .75-.07.97-.22c.23-.17.35-.36.35-.63c0-.19-.07-.34-.18-.47c-.12-.12-.35-.24-.67-.34l-.97-.3c-.48-.16-.84-.38-1.06-.68a1.58 1.58 0 0 1-.33-.97c0-.28.06-.52.18-.73c.12-.22.28-.4.46-.55c.22-.15.44-.26.71-.34q.39-.12.84-.12q.21 0 .45.03c.14.02.28.05.42.07c.14.04.26.07.38.11s.2.08.28.12c.09.05.16.1.2.16s.06.13.06.22v.32q0 .21-.15.21c-.05 0-.14-.03-.26-.08c-.37-.17-.8-.26-1.27-.26c-.38 0-.66.06-.89.19c-.2.12-.31.32-.31.59c0 .19.07.35.2.47c.13.13.38.25.73.37l.95.3c.48.14.82.36 1.03.64q.3.405.3.93c0 .28-.06.54-.17.77c-.12.22-.28.42-.5.58c-.19.17-.44.29-.72.38s-.62.13-.95.13m1.25 3.24C17.89 17.14 14.71 18 12 18c-3.85 0-7.3-1.42-9.91-3.77c-.21-.19-.02-.44.23-.29c2.82 1.63 6.29 2.62 9.89 2.62c2.43 0 5.1-.5 7.55-1.56c.37-.15.68.26.32.53M21 14.5c-.29-.37-1.86-.18-2.57-.1c-.21.03-.24-.16-.05-.3c1.25-.87 3.31-.6 3.54-.33c.24.3-.06 2.36-1.23 3.34c-.19.15-.36.07-.28-.11c.27-.68.86-2.16.59-2.5"/>
    </svg>
  )

  // Azure (mdi:microsoft-azure)
  if (type === 'holo') return (
    <svg {...props}>
      <path fill={s.color} d="M13.05 4.24L6.56 18.05L2 18l5.09-8.76zm.7 1.09L22 19.76H6.74l9.3-1.66l-4.87-5.79z"/>
    </svg>
  )

  // GCP (logos:google-cloud) — multicolor
  if (type === 'polychrome') return (
    <svg {...props} viewBox="0 0 256 206">
      <path fill="#EA4335" d="m170.252 56.819l22.253-22.253l1.483-9.37C153.437-11.677 88.976-7.496 52.42 33.92C42.267 45.423 34.734 59.764 30.717 74.573l7.97-1.123l44.505-7.34l3.436-3.513c19.797-21.742 53.27-24.667 76.128-6.168z"/>
      <path fill="#4285F4" d="M224.205 73.918a100.25 100.25 0 0 0-30.217-48.722l-31.232 31.232a55.52 55.52 0 0 1 20.379 44.037v5.544c15.35 0 27.797 12.445 27.797 27.796c0 15.352-12.446 27.485-27.797 27.485h-55.671l-5.466 5.934v33.34l5.466 5.231h55.67c39.93.311 72.553-31.494 72.864-71.424a72.3 72.3 0 0 0-31.793-60.453"/>
      <path fill="#34A853" d="M71.87 205.796h55.593V161.29H71.87a27.3 27.3 0 0 1-11.399-2.498l-7.887 2.42l-22.409 22.253l-1.952 7.574c12.567 9.489 27.9 14.825 43.647 14.757"/>
      <path fill="#FBBC05" d="M71.87 61.426C31.94 61.663-.237 94.227.001 134.158a72.3 72.3 0 0 0 28.222 56.88l32.248-32.246c-13.99-6.322-20.208-22.786-13.887-36.776s22.786-20.208 36.775-13.888a27.8 27.8 0 0 1 13.887 13.888l32.248-32.248A72.22 72.22 0 0 0 71.87 61.427"/>
    </svg>
  )

  // Docker (mdi:docker)
  if (type === 'glass') return (
    <svg {...props}>
      <path fill={s.color} d="M21.81 10.25c-.06-.04-.56-.43-1.64-.43c-.28 0-.56.03-.84.08c-.21-1.4-1.38-2.11-1.43-2.14l-.29-.17l-.18.27c-.24.36-.43.77-.51 1.19c-.2.8-.08 1.56.33 2.21c-.49.28-1.29.35-1.46.35H2.62c-.34 0-.62.28-.62.63c0 1.15.18 2.3.58 3.38c.45 1.19 1.13 2.07 2 2.61c.98.6 2.59.94 4.42.94c.79 0 1.61-.07 2.42-.22c1.12-.2 2.2-.59 3.19-1.16A8.3 8.3 0 0 0 16.78 16c1.05-1.17 1.67-2.5 2.12-3.65h.19c1.14 0 1.85-.46 2.24-.85c.26-.24.45-.53.59-.87l.08-.24zm-17.96.99h1.76c.08 0 .16-.07.16-.16V9.5c0-.08-.07-.16-.16-.16H3.85c-.09 0-.16.07-.16.16v1.58c.01.09.07.16.16.16m2.43 0h1.76c.08 0 .16-.07.16-.16V9.5c0-.08-.07-.16-.16-.16H6.28c-.09 0-.16.07-.16.16v1.58c.01.09.07.16.16.16m2.47 0h1.75c.1 0 .17-.07.17-.16V9.5c0-.08-.06-.16-.17-.16H8.75c-.08 0-.15.07-.15.16v1.58c0 .09.06.16.15.16m2.44 0h1.77c.08 0 .15-.07.15-.16V9.5c0-.08-.06-.16-.15-.16h-1.77c-.08 0-.15.07-.15.16v1.58c0 .09.07.16.15.16M6.28 9h1.76c.08 0 .16-.09.16-.18V7.25c0-.09-.07-.16-.16-.16H6.28c-.09 0-.16.06-.16.16v1.57c.01.09.07.18.16.18m2.47 0h1.75c.1 0 .17-.09.17-.18V7.25c0-.09-.06-.16-.17-.16H8.75c-.08 0-.15.06-.15.16v1.57c0 .09.06.18.15.18m2.44 0h1.77c.08 0 .15-.09.15-.18V7.25c0-.09-.07-.16-.15-.16h-1.77c-.08 0-.15.06-.15.16v1.57c0 .09.07.18.15.18m0-2.28h1.77c.08 0 .15-.07.15-.16V5c0-.1-.07-.17-.15-.17h-1.77c-.08 0-.15.06-.15.17v1.56c0 .08.07.16.15.16m2.46 4.52h1.76c.09 0 .16-.07.16-.16V9.5c0-.08-.07-.16-.16-.16h-1.76c-.08 0-.15.07-.15.16v1.58c0 .09.07.16.15.16"/>
    </svg>
  )

  // Kubernetes (mdi:kubernetes)
  return (
    <svg {...props}>
      <path fill={s.color} d="M13.95 13.5h-.23c-.18.11-.26.32-.18.5l.86 2.11c.83-.53 1.46-1.32 1.79-2.25l-2.23-.36zm-3.45.29a.415.415 0 0 0-.38-.29h-.08l-2.22.37c.33.92.96 1.7 1.79 2.23l.85-2.07V14c.04-.05.04-.14.04-.21m1.83.81a.38.38 0 0 0-.51-.15c-.07.05-.12.08-.15.15h-.01l-1.09 1.97c.78.26 1.62.31 2.43.12c.14-.03.29-.07.43-.12l-1.09-1.97zm3.45-4.57L14.1 11.5l.01.03a.37.37 0 0 0-.04.53q.075.09.18.12l.01.01l2.17.62c.07-.97-.14-1.95-.65-2.78m-3.11.16c.01.21.18.37.39.36c.08 0 .15-.02.21-.05h.01l1.83-1.31a4.45 4.45 0 0 0-2.57-1.24zm-1.94.31c.17.11.4.08.52-.09c.05-.06.07-.13.08-.21h.01l.12-2.25c-.15.02-.3.05-.46.08c-.8.18-1.54.58-2.12 1.16l1.84 1.31zm-.99 1.69c.2-.05.32-.26.26-.46c0-.08-.05-.14-.11-.19v-.01L8.21 10c-.52.86-.74 1.84-.63 2.82l2.16-.62zm1.64.66l.62.3l.62-.3l.15-.67l-.43-.53h-.69l-.43.53zm10.89 1.32L20.5 6.5c-.09-.42-.37-.76-.74-.94l-7.17-3.43c-.37-.17-.81-.17-1.19 0L4.24 5.56c-.37.18-.65.52-.74.94l-1.77 7.67c-.05.2-.05.4 0 .59c.01.06.03.12.05.18c.03.09.08.19.13.27c.03.04.05.08.09.11l4.95 6.18c.02 0 .05.04.05.06c.1.09.19.16.28.22c.12.08.26.14.4.17c.11.05.23.05.32.05h8.12c.07 0 .14-.03.2-.05c.05-.01.1-.03.14-.04c.04-.02.07-.03.11-.05c.05-.02.1-.05.15-.08c.12-.08.23-.18.33-.28l.15-.2l4.8-5.98c.1-.12.17-.25.22-.38c.02-.06.04-.12.05-.18c.05-.19.05-.4 0-.59m-7.43 2.99c.02.06.04.12.07.17c-.04.08-.06.17-.03.26c.12.24.23.46.38.68c.08.11.16.23.24.34c0 .03.03.08.04.12c.12.2.06.46-.15.59s-.47.05-.59-.15c-.01-.03-.02-.05-.03-.08c-.02-.03-.04-.09-.06-.09c-.05-.15-.09-.28-.12-.41c-.09-.25-.17-.49-.3-.72a.38.38 0 0 0-.21-.14l-.08-.16c-1.29.48-2.7.48-3.97-.01l-.1.18c-.07.01-.14.04-.19.09c-.14.24-.24.49-.33.77c-.03.13-.07.26-.12.4c-.02 0-.04.07-.06.1a.43.43 0 0 1-.81-.29c.01-.03.03-.05.04-.08c.01-.03.04-.08.04-.11c.09-.12.16-.23.24-.35c.16-.21.29-.45.39-.69a.54.54 0 0 0-.03-.25l.07-.18a5.6 5.6 0 0 1-2.47-3.09l-.2.03a.4.4 0 0 0-.23-.09c-.27.05-.51.13-.77.22c-.11.06-.24.11-.37.15c-.03.01-.07.02-.13.03a.44.44 0 0 1-.54-.27c-.07-.23.04-.47.28-.55c.02 0 .05-.01.08-.01v-.01h.01l.11-.02c.14-.04.28-.04.41-.04c.26 0 .52-.06.77-.12c.08-.05.14-.11.19-.19l.19-.05c-.21-1.36.1-2.73.86-3.87l-.14-.12c0-.09-.03-.18-.08-.25c-.2-.17-.41-.32-.64-.45q-.18-.09-.36-.21c-.02-.02-.06-.05-.08-.07l-.01-.01c-.2-.16-.25-.42-.11-.63c.09-.1.21-.15.35-.15q.165.015.3.12l.09.07c.1.09.19.2.28.3c.18.19.37.37.58.52c.08.04.17.05.26.03l.15.11c.75-.8 1.73-1.36 2.8-1.6c.25-.06.52-.1.78-.12l.01-.18a.45.45 0 0 0 .14-.23c.01-.26-.01-.52-.05-.77c-.03-.13-.05-.27-.06-.41V5.1c-.02-.24.15-.45.39-.48s.44.15.47.38v.22c-.01.14-.03.28-.06.41c-.04.25-.06.51-.05.77c.02.1.07.17.14.22l.01.19c1.36.12 2.62.73 3.56 1.72l.16-.12c.09.02.18.01.26-.03c.21-.15.41-.33.58-.52c.09-.1.18-.2.28-.3c.03-.02.07-.06.1-.06c.17-.18.44-.18.59 0c.19.16.18.43 0 .6c0 .02-.03.04-.06.06a2.5 2.5 0 0 1-.44.28c-.23.13-.45.28-.64.45c-.06.07-.09.15-.08.24l-.16.14a5.44 5.44 0 0 1 .88 3.86l.19.05c.04.08.11.14.19.18c.25.07.51.11.77.14h.41c.03.03.08.04.12.05c.24.03.4.25.37.49c-.05.23-.24.4-.48.37c-.03-.01-.07-.01-.07-.02v-.01c-.06 0-.1-.01-.14-.02q-.195-.06-.36-.15c-.26-.1-.5-.17-.77-.21c-.09 0-.17 0-.23.08c-.07-.01-.13-.02-.19-.03c-.41 1.31-1.31 2.41-2.47 3.11"/>
    </svg>
  )
}

// ─── Enhancement watermark variants ───

function EnhancementWatermark({ type, isSm, glassPos }: { type: CardEnhancement; isSm: boolean; glassPos?: { x: number; y: number } }) {
  if (type === 'none') return null

  // AWS foil — 整齐排列水印
  if (type === 'foil') {
    const iconSize = isSm ? 14 : 20
    const cols = 3
    const rows = 4
    return (
      <>
        {Array.from({ length: rows * cols }, (_, i) => {
          const row = Math.floor(i / cols)
          const col = i % cols
          return (
            <div
              key={i}
              className="absolute"
              style={{
                top: `${10 + row * 24}%`,
                left: `${8 + col * 32}%`,
                opacity: 0.65,
                transform: `rotate(-20deg) scale(${isSm ? 1 : 1.4})`,
              }}
            >
              <EnhancementIcon type={type} size={iconSize} />
            </div>
          )
        })}
      </>
    )
  }

  // Azure holo — 电路纹路
  if (type === 'holo') {
    const c = '#0078D4'
    const w = isSm ? 120 : 160
    const h = isSm ? 160 : 240
    return (
      <svg
        className="absolute inset-0"
        width={w}
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        fill="none"
        style={{ opacity: 0.2 }}
      >
        {/* 主干线 */}
        <path d={`M12 0 V28 H40 V60`} stroke={c} strokeWidth="1.2" />
        <path d={`M${w-20} 0 V18 H${w-50} V48 H${w-30} V72`} stroke={c} strokeWidth="1.2" />
        <path d={`M0 ${h*0.4} H24 V${h*0.55} H52`} stroke={c} strokeWidth="1.2" />
        <path d={`M${w} ${h*0.35} H${w-28} V${h*0.5} H${w-56} V${h*0.65}`} stroke={c} strokeWidth="1.2" />
        <path d={`M30 ${h} V${h-30} H60 V${h-55}`} stroke={c} strokeWidth="1.2" />
        <path d={`M${w-16} ${h} V${h-24} H${w-44} V${h-50} H${w-70}`} stroke={c} strokeWidth="1.2" />
        {/* 分支 */}
        <path d={`M40 28 H68`} stroke={c} strokeWidth="0.8" />
        <path d={`M24 ${h*0.55} H24 V${h*0.7} H44`} stroke={c} strokeWidth="0.8" />
        <path d={`M${w-50} 48 V70`} stroke={c} strokeWidth="0.8" />
        <path d={`M60 ${h-55} H80 V${h-75}`} stroke={c} strokeWidth="0.8" />
        {/* 节点 */}
        {[
          [12, 28], [40, 28], [40, 60], [68, 28],
          [w-20, 18], [w-50, 48], [w-30, 72], [w-50, 70],
          [24, h*0.4], [24, h*0.55], [52, h*0.55], [44, h*0.7],
          [w-28, h*0.35], [w-56, h*0.5], [w-56, h*0.65],
          [30, h-30], [60, h-30], [60, h-55], [80, h-75],
          [w-16, h-24], [w-44, h-24], [w-44, h-50], [w-70, h-50],
        ].map(([cx, cy], i) => (
          <circle key={i} cx={cx} cy={cy} r="2" fill={c} />
        ))}
        {/* Azure icon 左下角 */}
        <g transform={`translate(${isSm ? 4 : 6}, ${h - (isSm ? 36 : 50)}) scale(${isSm ? 1.2 : 1.7})`} opacity="0.85">
          <path fill={c} d="M13.05 4.24L6.56 18.05L2 18l5.09-8.76zm.7 1.09L22 19.76H6.74l9.3-1.66l-4.87-5.79z"/>
        </g>
      </svg>
    )
  }

  // GCP polychrome — 闪卡效果
  if (type === 'polychrome') {
    return (
      <>
        {/* 彩虹渐变底层 */}
        <div
          className="absolute inset-0 rounded-[10px]"
          style={{
            background: 'linear-gradient(135deg, rgba(255,0,0,0.3), rgba(255,154,0,0.3), rgba(230,230,0,0.3), rgba(0,220,60,0.3), rgba(0,200,220,0.3), rgba(47,128,237,0.3), rgba(160,40,255,0.3), rgba(255,0,80,0.3))',
            animation: 'poly-hue 4s linear infinite',
          }}
        />
        {/* GCP icon */}
        <div
          className="absolute"
          style={{
            top: isSm ? 28 : 42,
            left: isSm ? 4 : 6,
            opacity: 0.5,
            transform: `rotate(-15deg) scale(${isSm ? 1 : 1.4})`,
            animation: 'poly-hue 4s linear infinite',
          }}
        >
          <EnhancementIcon type={type} size={isSm ? 16 : 22} />
        </div>
      </>
    )
  }

  // Docker glass — Fluid Glass
  if (type === 'glass') {
    return (
      <>
        {/* 流体高光 — 跟随鼠标 */}
        <div
          className="absolute inset-0 rounded-[10px]"
          style={{
            background: `radial-gradient(ellipse 80% 60% at ${glassPos?.x ?? 30}% ${glassPos?.y ?? 20}%, rgba(255,255,255,0.22), transparent 70%)`,
            transition: 'background 0.15s ease-out',
          }}
        />
        {/* 折射彩虹边缘 */}
        <div
          className="absolute inset-0 rounded-[10px]"
          style={{
            background: `
              linear-gradient(160deg, rgba(255,255,255,0.25) 0%, transparent 20%),
              linear-gradient(340deg, rgba(200,220,255,0.1) 0%, transparent 25%)
            `,
          }}
        />
        {/* 厚度：顶部亮边 */}
        <div
          className="absolute left-0 right-0 top-0 rounded-t-[10px]"
          style={{
            height: isSm ? 3 : 5,
            background: 'linear-gradient(180deg, rgba(255,255,255,0.4), transparent)',
          }}
        />
        {/* 厚度：底部暗边 */}
        <div
          className="absolute left-0 right-0 bottom-0 rounded-b-[10px]"
          style={{
            height: isSm ? 5 : 8,
            background: 'linear-gradient(0deg, rgba(0,0,0,0.25), transparent)',
          }}
        />
        {/* Docker icon */}
        <div
          className="absolute"
          style={{
            bottom: '5%',
            right: '0%',
            opacity: 0.15,
            transform: `scale(${isSm ? 1 : 1.4})`,
          }}
        >
          <EnhancementIcon type={type} size={isSm ? 48 : 68} />
        </div>
      </>
    )
  }

  // K8s steel — 铁牌 + 右上角露出部分 logo 阳刻
  const steelSize = isSm ? 80 : 110
  return (
    <div
      className="absolute"
      style={{
        top: isSm ? -28 : -38,
        right: isSm ? -28 : -38,
        width: steelSize,
        height: steelSize,
        filter: 'drop-shadow(1px 1px 0 rgba(255,255,255,0.15)) drop-shadow(-1px -1px 0 rgba(0,0,0,0.4))',
        opacity: 0.25,
      }}
    >
      <EnhancementIcon type={type} size={steelSize} />
    </div>
  )
}

export function PokerCard({
  card,
  selected = false,
  scoring = false,
  onClick,
  disabled = false,
  index = 0,
  size = 'md',
}: Props) {
  const { t } = useTranslation()
  const [hovered, setHovered] = useState(false)
  const [glassPos, setGlassPos] = useState({ x: 30, y: 20 })

  const handleMouseEnter = useCallback(() => setHovered(true), [])
  const handleMouseLeave = useCallback(() => setHovered(false), [])

  const suit = SUIT_DISPLAY[card.suit]
  const rankLabel = RANK_DISPLAY[card.rank]
  const enhancement = ENHANCEMENT_STYLES[card.enhancement]
  const isWild = card.suit === 'wild'
  const hasEnhancement = card.enhancement !== 'none'
  const isSm = size === 'sm'
  const faceImage = getCardFaceIconUrl(card.suit, card.rank)
  const mascotLabel = CARD_MASCOT_LABELS[`${card.suit}_${card.rank}`]

  // Domain color for border glow
  const isSteel = card.enhancement === 'steel'
  const isGlass = card.enhancement === 'glass'
  const isLightBg = !isSteel && !isGlass
  const domainColor = suit.color

  return (
    <CardShell
      size={size}
      className={cn(
        'cursor-pointer select-none',
        isSm ? 'p-2' : 'p-3',
      )}
      background={
        isSteel ? 'linear-gradient(160deg, #7a8090 0%, #5c6272 20%, #6e7585 50%, #4e5564 80%, #6a7080 100%)'
        : isGlass ? 'rgba(20,16,36,0.25)'
        : '#f0ebe1'
      }
      disabled={disabled}
      onClick={onClick}
      style={{
        border: isSteel ? '2px solid #9aa0ad'
          : isGlass ? '1.5px solid rgba(255,255,255,0.2)'
          : '1px solid rgba(0,0,0,0.08)',
        borderRadius: 12,
        transform: selected ? 'translateY(-8px)' : (!disabled && hovered) ? 'translateY(-4px)' : undefined,
        boxShadow: selected
          ? '0 0 12px rgba(245,166,35,0.3), 0 8px 24px rgba(0,0,0,0.15)'
          : scoring
          ? (isLightBg
            ? '0 0 8px rgba(46,204,113,0.2), 0 4px 12px rgba(0,0,0,0.1)'
            : '0 0 16px rgba(46,204,113,0.35), inset 0 1px 0 rgba(255,255,255,0.05), 0 4px 12px rgba(0,0,0,0.3)')
          : (!disabled && hovered)
            ? (isSteel
              ? 'inset 0 1px 0 rgba(255,255,255,0.3), inset 0 -1px 0 rgba(0,0,0,0.25), 0 8px 24px rgba(0,0,0,0.5)'
              : isLightBg
                ? '0 2px 8px rgba(0,0,0,0.15), 0 8px 24px rgba(0,0,0,0.1)'
                : `inset 0 1px 0 rgba(255,255,255,0.05), 0 0 16px ${domainColor}50, 0 8px 24px rgba(0,0,0,0.5)`)
          : isSteel
            ? 'inset 0 1px 0 rgba(255,255,255,0.25), inset 0 -1px 0 rgba(0,0,0,0.2), 0 4px 12px rgba(0,0,0,0.4)'
          : isGlass
            ? `inset 0 1px 0 rgba(255,255,255,0.3), inset 0 -1px 0 rgba(0,0,0,0.15), inset 1px 0 0 rgba(255,255,255,0.1), inset -1px 0 0 rgba(255,255,255,0.05), 0 8px 32px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.2)`
            : isLightBg
              ? `0 1px 3px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.08)`
              : `inset 0 1px 0 rgba(255,255,255,0.05), 0 0 10px ${domainColor}30, 0 4px 12px rgba(0,0,0,0.3)`,
        backdropFilter: isGlass ? 'blur(12px) saturate(1.3)' : undefined,
        WebkitBackdropFilter: isGlass ? 'blur(12px) saturate(1.3)' : undefined,
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        animation: `card-deal 300ms ease-out ${index * 50}ms both`,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={isGlass ? (e) => {
        const rect = e.currentTarget.getBoundingClientRect()
        setGlassPos({
          x: ((e.clientX - rect.left) / rect.width) * 100,
          y: ((e.clientY - rect.top) / rect.height) * 100,
        })
      } : undefined}
    >
      {/* Enhancement overlay */}
      {hasEnhancement && !isLightBg && (
        <div className={cn('absolute inset-0 rounded-[10px] pointer-events-none z-[1]', enhancement.overlay)} />
      )}

      {/* ─── Top-left corner: suit icon + rank ─── */}
      <div className="flex items-center gap-1 mb-1 relative z-[2]">
        <img
          src={suit.iconUrl}
          alt={suit.name}
          className={cn('object-contain', isSm ? 'w-3 h-3' : 'w-4 h-4')}
          style={{ filter: isLightBg ? 'none' : `drop-shadow(0 0 2px ${domainColor}40)` }}
        />
        <span
          className={cn('font-black font-mono leading-none', isSm ? 'text-[16px]' : 'text-[20px]')}
          style={{ color: domainColor, textShadow: isLightBg ? 'none' : `0 0 8px ${domainColor}40` }}
        >
          {rankLabel}
        </span>
      </div>

      {/* ─── Center: icon + name + suit label ─── */}
      <div className="flex-1 flex flex-col items-center justify-center relative z-[2] gap-0.5">
        {/* Icon */}
        <div
          className="flex items-center justify-center rounded-lg"
          style={{
            width: isSm ? 44 : 60,
            height: isSm ? 36 : 48,
            backgroundColor: isLightBg ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.03)',
          }}
        >
          {faceImage ? (
            <img
              src={faceImage}
              alt={card.name}
              className={cn('object-contain', isSm ? 'w-[28px] h-[28px]' : 'w-[40px] h-[40px]')}
              loading="lazy"
              style={{ filter: isLightBg ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.15))' : 'drop-shadow(0 1px 3px rgba(0,0,0,0.5))' }}
            />
          ) : (
            <img
              src={suit.iconUrl}
              alt={suit.name}
              className={cn('object-contain', isSm ? 'w-[20px] h-[20px]' : 'w-[28px] h-[28px]')}
              style={{ opacity: 0.5 }}
            />
          )}
        </div>

        {/* Card name */}
        <div
          className={cn(
            'font-semibold text-center truncate w-full leading-tight',
            isSm ? 'text-[10px]' : 'text-sm',
          )}
          style={{ color: mascotLabel ? (isLightBg ? '#2a2a2a' : '#e8e8e8') : (isLightBg ? 'rgba(80,70,100,0.55)' : 'rgba(200,190,220,0.55)') }}
        >
          {mascotLabel ?? card.name}
        </div>

        {/* Suit name */}
        <div
          className={cn(
            'font-bold uppercase tracking-wide text-center',
            isSm ? 'text-[7px]' : 'text-[9px]',
          )}
          style={{ color: `${domainColor}90` }}
        >
          {suit.name}
        </div>
      </div>

      {/* ─── Stats row: chips (md only) ─── */}
      {!isSm && (
        <div className="flex justify-center gap-2 mb-1 relative z-[2]">
          <span
            className="font-mono text-xs font-bold"
            style={{ color: isLightBg ? '#b07d10' : '#f5a623', textShadow: isLightBg ? 'none' : '0 0 8px rgba(245,166,35,0.5)' }}
          >
            +{card.chipValue} Chips
          </span>
          {card.bonusChips > 0 && (
            <span className="font-mono text-xs" style={{ color: '#3b82f6' }}>
              +{card.bonusChips}
            </span>
          )}
          {card.bonusMult > 0 && (
            <span className="font-mono text-xs" style={{ color: '#e74c3c' }}>
              ×{card.bonusMult}
            </span>
          )}
        </div>
      )}

      {/* ─── Enhancement watermark ─── */}
      {hasEnhancement && (
        <div className="absolute inset-0 pointer-events-none z-[1] overflow-hidden rounded-[10px]">
          <EnhancementWatermark type={card.enhancement} isSm={isSm} glassPos={isGlass ? glassPos : undefined} />
        </div>
      )}

      {/* ─── Enhancement hover tooltip ─── */}
      {hasEnhancement && hovered && (
        <div
          className="absolute left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-2 py-1 rounded-md pointer-events-none z-[20] whitespace-nowrap"
          style={{
            bottom: -28,
            background: 'rgba(20,16,36,0.92)',
            border: `1px solid ${enhancement.color}50`,
            boxShadow: `0 4px 12px rgba(0,0,0,0.5), ${enhancement.glow}`,
          }}
        >
          <span className="font-bold text-[10px]" style={{ color: enhancement.color }}>
            {enhancement.techLabel}
          </span>
          <span className="text-[10px]" style={{ color: 'rgba(220,210,240,0.8)' }}>
            {enhancement.effect.startsWith('card.') ? t(enhancement.effect as 'card.glassEffect' | 'card.steelEffect') : enhancement.effect}
          </span>
        </div>
      )}

      {/* Wild indicator */}
      {isWild && (
        <div
          className={cn(
            'absolute px-1.5 py-0.5 rounded-sm font-bold z-[3]',
            isSm ? 'bottom-1 left-1 text-[7px]' : 'bottom-1.5 left-1.5 text-[8px]',
          )}
          style={{
            color: domainColor,
            background: isLightBg ? `${domainColor}18` : `${domainColor}20`,
            border: isLightBg ? `1px solid ${domainColor}40` : 'none',
          }}
        >
          WILD
        </div>
      )}

      {/* ─── Bottom-right corner: suit icon + rank (mirrored, like a real card) ─── */}
      <div
        className={cn('absolute flex items-center gap-1 z-[2]', isSm ? 'bottom-1.5 right-2' : 'bottom-2 right-3')}
        style={{ transform: 'rotate(180deg)' }}
      >
        <img
          src={suit.iconUrl}
          alt=""
          className={cn('object-contain', isSm ? 'w-3 h-3' : 'w-4 h-4')}
          style={{ filter: isLightBg ? 'none' : `drop-shadow(0 0 2px ${domainColor}40)` }}
        />
        <span
          className={cn('font-black font-mono leading-none', isSm ? 'text-[16px]' : 'text-[20px]')}
          style={{ color: domainColor, textShadow: isLightBg ? 'none' : `0 0 8px ${domainColor}40` }}
        >
          {rankLabel}
        </span>
      </div>
    </CardShell>
  )
}
